import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, MessageSquare } from "lucide-react";
import ContactDialog, { ObfuscatedEmail } from "@/components/ContactDialog";
import usePromoStatus from "@/lib/usePromoStatus";

// Common pricing-page objections. Written in institutional voice — direct,
// no fluff, no salesy "!!!" energy. Ordered by frequency of the question.
const FAQ = [
  {
    qKey: "pricingFaqUi.neverTradedQ",
    aKey: "pricingFaqUi.neverTradedA",
  },
  {
    qKey: "pricingFaqUi.needBrokerQ",
    aKey: "pricingFaqUi.needBrokerA",
  },
  {
    qKey: "pricingFaqUi.financialAdviceQ",
    aKey: "pricingFaqUi.financialAdviceA",
  },
  {
    qKey: "pricingFaqUi.refundPolicyQ",
    aKey: "pricingFaqUi.refundPolicyA",
  },
  {
    qKey: "pricingFaqUi.monthlyOrOnetimeQ",
    aKey: "pricingFaqUi.monthlyOrOnetimeA",
  },
  {
    qKey: "pricingFaqUi.upgradeBasicQ",
    aKey: "pricingFaqUi.upgradeBasicA",
  },
  {
    qKey: "pricingFaqUi.discordIncludedQ",
    aKey: "pricingFaqUi.discordIncludedA",
  },
  {
    qKey: "pricingFaqUi.differentFromYoutubeQ",
    aKey: "pricingFaqUi.differentFromYoutubeA",
  },
];

export default function PricingFAQ() {
  const { t } = useTranslation();
  const { params: P, loyaltyOn, referralOn } = usePromoStatus();
  const answer = (k) => k !== "pricingFaqUi.upgradeBasicA" ? t(k, P)
    : [t(loyaltyOn ? k : "pricingFaqUi.upgradeBasicAFull", P), referralOn && t("pricingFaqUi.upgradeBasicAReferral", P)].filter(Boolean).join(" ");
  const [openId, setOpenId] = useState(0);
  const [contactOpen, setContactOpen] = useState(false);

  return (
    <section
      data-testid="pricing-faq"
      className="max-w-3xl mx-auto px-6 sm:px-10 lg:px-16 pb-24"
    >
      <div className="text-xs uppercase tracking-[0.28em] text-blue-800 font-semibold mb-3">
        {t("pricingFaqUi.sectionLabel")}
      </div>
      <h2 className="font-display font-black text-3xl sm:text-4xl text-slate-900 tracking-tighter leading-tight mb-2">
        {t("pricingFaqUi.heading")}
      </h2>
      <p className="text-slate-600 mb-6">
        {t("pricingFaqUi.subheading")}
      </p>
      <div className="mb-10 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => setContactOpen(true)}
          data-testid="pricing-faq-contact-btn"
          className="inline-flex items-center gap-2 rounded-md bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold px-4 py-2.5 transition-colors"
        >
          <MessageSquare size={14} /> {t("pricingFaqUi.messageUsBtn")}
        </button>
        <span className="text-xs text-slate-500">
          {t("pricingFaqUi.orEmail")} <ObfuscatedEmail className="text-blue-800 underline underline-offset-2" />
        </span>
      </div>

      <ul className="space-y-3">
        {FAQ.map((item, idx) => {
          const isOpen = openId === idx;
          return (
            <li
              key={idx}
              data-testid="pricing-faq-item"
              className={`rounded-xl border transition-colors ${
                isOpen
                  ? "border-blue-900/20 bg-white shadow-sm"
                  : "border-slate-200 bg-white hover:border-slate-300"
              }`}
            >
              <button
                type="button"
                onClick={() => setOpenId(isOpen ? -1 : idx)}
                data-testid={`pricing-faq-toggle-${idx}`}
                aria-expanded={isOpen}
                className="w-full flex items-start justify-between gap-4 text-left px-5 sm:px-6 py-4 sm:py-5"
              >
                <span className={`font-semibold text-base sm:text-lg ${isOpen ? "text-slate-900" : "text-slate-800"}`}>
                  {t(item.qKey)}
                </span>
                <ChevronDown
                  size={20}
                  className={`shrink-0 mt-1 text-slate-500 transition-transform ${isOpen ? "rotate-180 text-blue-800" : ""}`}
                />
              </button>
              {isOpen && (
                <div className="px-5 sm:px-6 pb-5 sm:pb-6 -mt-1 text-sm text-slate-600 leading-relaxed">
                  {answer(item.aKey)}
                </div>
              )}
            </li>
          );
        })}
      </ul>

      <ContactDialog open={contactOpen} onClose={() => setContactOpen(false)} subject={t("pricingFaqUi.contactSubject")} />
    </section>
  );
}
